import { Fragment, useMemo } from "react"

import { App, Avatar, Col, Flex, Row, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Private from "../../../../Components/Private"
import Icon from "../../../../Icon"
import { modalConfig } from "../../../../theme"
import Creators from "../../reducer"
import { getColumns } from "../Table/columns"

const { Text, Link } = Typography

const dataList = {
  drawer: [
    {
      id: "id",
      label: "Request ID"
    },
    {
      id: "request_type",
      label: "Request type"
    },
    {
      id: "request_status",
      label: "Request status"
    },
    {
      id: "provider_status",
      label: "Provider status"
    },
    {
      id: "request_date_timestamp",
      label: "Request date"
    },
    {
      id: "update_date_timestamp",
      label: "Update status date"
    },
    {
      id: "business_account",
      label: "Business account"
    },
    {
      id: "user_name",
      label: "User name"
    },
    {
      id: "user_email",
      label: "User email"
    },
    {
      id: "alert_id",
      label: "Alert ID"
    },
    {
      id: "alert_service",
      label: "Alert source"
    },
    {
      id: "alert_timestamp",
      label: "Alert date"
    },
    {
      id: "alert_status",
      label: "Alerts status"
    },
    {
      id: "transaction_id",
      label: "Transaction ID"
    },
    {
      id: "request_amount",
      label: "Alert cost"
    },
    {
      id: "request_reason",
      label: "Request reason"
    },
    {
      id: "credit_request_details",
      label: "Details"
    },
    {
      id: "rejection_reason",
      label: "Rejection reason"
    },
    {
      id: "rejection_reason_details",
      label: "Rejection reason details"
    }
  ]
}

const statusMap = {
  Pending: "warning",
  Paid: "success",
  "Not paid": "danger"
}

export const OverviewInfo = ({ data = {}, type }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const isAdmin = useSelector((state) => state.auth.isAdmin)
  const ba_role = useSelector((state) => state.auth.userInfo?.ba_role?.id)
  const theme = useSelector((state) => state.nav.theme)
  const filterOptions = useSelector((state) => state.alerts.filterOptions)

  const businessAccountRole = isAdmin ? null : ba_role
  const list = dataList?.[type] || []

  const changeStatusConfirm = (data) => {
    modal.confirm({
      ...modalConfig("info"),
      //centered: !isMobile,

      title: t("Alert status"),
      content: (
        <>
          <Text type="secondary">{t("Are you sure you want to change alert status?")}</Text>
          <br />
          <Text type="secondary">{t("Changing your alert status is an irreversible action.")}</Text>
        </>
      ),
      cancelText: t("Go back"),
      okText: t("Change status"),
      cancelButtonProps: { size: "large" },
      okButtonProps: { size: "large" },
      onOk() {
        dispatch(Creators.updateAlertRequest(data))
      }
    })
  }

  const columnRender = useMemo(() => {
    const handleChangeStatus = ({ alert_id, newStatus }) => {
      changeStatusConfirm({ alert_id, alert_status: newStatus })
    }
    let options = filterOptions?.["alert_status"] || []

    return {
      ...getColumns({
        t,
        options,
        handleChangeStatus,
        theme,
        isAdmin,
        disabledStatus: true,
        strong: true
      }),
      amount: {
        render: (_, record) => (
          <Text strong>
            <Private>{`${record?.currency || ""} ${record?.amount}`}</Private>
          </Text>
        )
      },
      alert_timestamp: {
        render: (_) => <Text strong>{_ ? dayjs(_).format("DD MMM YYYY h:mm A [(UTC)]") : "—"}</Text>
      },
      transaction_timestamp: {
        render: (_) => <Text strong>{_ ? dayjs(_).format("DD MMM YYYY h:mm A [(UTC)]") : "—"}</Text>
      },
      business_account: {
        render: (_) => (
          <Flex gap={4} align="center">
            <Avatar
              className="user_avatar"
              size={16}
              style={{
                backgroundColor: _?.photo ? "transparent" : "#225AEA",
                borderRadius: "4px",
                border: "none",
                fontSize: 12,
                marginRight: 4,
                minWidth: 16
              }}
              src={_?.photo || null}
            >
              {!_?.photo ? _?.business_account?.[0] : null}
            </Avatar>
            <Text
              strong
              copyable={{ tooltips: false }}
              ellipsis={{ tooltip: _?.business_account }}
              style={{ maxWidth: "100%" }}
            >
              <Private>{_?.business_account}</Private>
            </Text>
          </Flex>
        )
      },
      alert_id: null,
      transaction_id: {
        render: (_, record) =>
          _?.length ? (
            <Flex gap={4} vertical={true}>
              {_?.map((id, idx) => (
                <Flex gap={4} align="center">
                  <a
                    key={id}
                    href={record?.["transaction_link"]?.[idx] || record?.["transaction_url"]?.[idx]}
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                    rel="noreferrer"
                  >
                    <Private>{id}</Private>
                  </a>
                  <Text copyable={{ tooltips: false, text: id }} />
                </Flex>
              ))}
            </Flex>
          ) : (
            "—"
          )
      }
    }
  }, [])

  return (
    <Row gutter={[32, 6]}>
      {list
        ?.filter((el) =>
          data?.rejection_reason !== "Other" ? el?.id !== "rejection_reason_details" : true
        )
        ?.map((el) => (
          <Fragment key={el?.id}>
            <Col span={10}>
              <Flex gap={4} align="center">
                <Text type="secondary">{t(el?.label)}</Text>
                {el.tooltip && (
                  <Tooltip title={t(el.tooltip)}>
                    <Icon type="circle_info" style={{ fontSize: 14, color: "#7A7A83" }} />
                  </Tooltip>
                )}
              </Flex>
            </Col>
            <Col span={14}>
              {columnRender[el?.id]?.render ? (
                columnRender[el?.id]?.render(data?.[el?.id], data)
              ) : data?.[el?.id] ? (
                <Flex gap={4} align="center" style={{ maxWidth: "100%" }}>
                  <Text
                    title={typeof data?.[el?.id] === "string" ? data?.[el?.id] : undefined}
                    style={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                    strong
                  >
                    <Private>{data?.[el?.id]}</Private>
                  </Text>
                  <Text copyable={{ tooltips: false, text: data?.[el?.id] }} />
                </Flex>
              ) : (
                <Text strong>—</Text>
              )}
            </Col>
          </Fragment>
        ))}
    </Row>
  )
}
